// src/pages/expert/AcquisitionProposals.jsx
// Espace Art Advisor : propositions d'acquisition (sélections d'œuvres NON détenues),
// prix demandé + argumentaire par œuvre, lien de partage 14 jours, réactions du client.
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../context/AuthContext";
import { newProposalToken, REACTION_LABELS } from "../../lib/proposalApi";

const card = { background: "#fff", border: "1px solid #e5e3df", borderRadius: 4, padding: 16 };
const input = { padding: "8px 10px", border: "1px solid #e5e3df", borderRadius: 2, fontSize: 13, fontFamily: "system-ui, sans-serif", outline: "none", width: "100%", boxSizing: "border-box" };
const btn = { padding: "8px 14px", background: "#1a1a1a", color: "#fff", border: "none", borderRadius: 2, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "system-ui, sans-serif" };
const btnGhost = { ...btn, background: "#fff", color: "#1a1a1a", border: "1px solid #1a1a1a" };
const eyebrow = { fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "#8b7355", fontWeight: 700, fontFamily: "system-ui, sans-serif" };

const linkStatus = (l) => {
  if (l.revoked_at) return "Révoqué";
  if (new Date(l.expires_at).getTime() <= Date.now()) return "Expiré";
  if (l.submitted_at) return "Réponses reçues";
  return "Actif";
};

function ItemRow({ artwork, meta, onSave }) {
  const [price, setPrice] = useState(meta?.asking_price ?? "");
  const [why, setWhy] = useState(meta?.rationale || "");
  const dirty = String(price) !== String(meta?.asking_price ?? "") || why !== (meta?.rationale || "");
  return (
    <div style={{ ...card, display: "grid", gridTemplateColumns: "88px 1fr", gap: 14 }}>
      {artwork.image_url ? (
        <img src={artwork.image_url} alt="" style={{ width: 88, height: 88, objectFit: "cover", borderRadius: 2 }} />
      ) : (
        <div style={{ width: 88, height: 88, background: "#f3f0ea", borderRadius: 2 }} />
      )}
      <div style={{ display: "grid", gap: 8, fontFamily: "system-ui, sans-serif" }}>
        <div><strong>{artwork.title}</strong> <span style={{ color: "#6b6b6b" }}>· {artwork.artist}{artwork.year ? `, ${artwork.year}` : ""}</span></div>
        <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 8 }}>
          <input style={input} type="number" min="0" placeholder="Prix demandé (€)" value={price} onChange={(e) => setPrice(e.target.value)} />
          <input style={input} placeholder="Pourquoi cette œuvre pour ce client ?" value={why} onChange={(e) => setWhy(e.target.value)} />
        </div>
        {dirty && (
          <div>
            <button style={btn} onClick={() => onSave(artwork.id, price === "" ? null : Number(price), why)}>Enregistrer</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AcquisitionProposals() {
  const { user } = useAuth() || {};
  const navigate = useNavigate();
  const [proposals, setProposals] = useState([]);
  const [clients, setClients] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [form, setForm] = useState({ title: "", client: "" });
  const [artworks, setArtworks] = useState([]);
  const [metas, setMetas] = useState({});
  const [links, setLinks] = useState([]);
  const [reactions, setReactions] = useState({});
  const [msg, setMsg] = useState("");

  const loadList = useCallback(async () => {
    if (!user?.id) return;
    const [{ data: cols }, { data: ents }] = await Promise.all([
      supabase.from("collections").select("id,title,description,created_at").eq("client_id", user.id).eq("collection_type", "proposal").order("created_at", { ascending: false }),
      supabase.from("advisor_client_entities").select("id,display_name").eq("primary_advisor_id", user.id).neq("status", "archived"),
    ]);
    setProposals(cols || []);
    setClients(ents || []);
  }, [user?.id]);

  const loadDetail = useCallback(async (id) => {
    const [{ data: arts }, { data: items }, { data: lks }] = await Promise.all([
      supabase.from("artworks").select("id,title,artist,year,image_url").eq("collection_id", id),
      supabase.from("proposal_items").select("*").eq("collection_id", id),
      supabase.from("proposal_links").select("*").eq("collection_id", id).order("created_at", { ascending: false }),
    ]);
    setArtworks(arts || []);
    setMetas(Object.fromEntries((items || []).map((m) => [m.artwork_id, m])));
    setLinks(lks || []);
    const ids = (lks || []).map((l) => l.id);
    if (ids.length) {
      const { data: rx } = await supabase.from("proposal_reactions").select("link_id,artwork_id,reaction,comment").in("link_id", ids);
      const by = {};
      (rx || []).forEach((r) => { (by[r.link_id] ||= []).push(r); });
      setReactions(by);
    } else setReactions({});
  }, []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { if (openId) loadDetail(openId); }, [openId, loadDetail]);

  const create = async () => {
    const title = form.title.trim();
    if (!title) { setMsg("Donnez un nom à la proposition."); return; }
    const { data, error } = await supabase.from("collections")
      .insert({ title, client_id: user.id, collection_type: "proposal", description: form.client.trim() || null })
      .select("id").single();
    if (error) { setMsg(error.message); return; }
    setForm({ title: "", client: "" });
    setMsg("");
    await loadList();
    setOpenId(data.id);
  };

  const saveItem = async (artworkId, asking_price, rationale) => {
    const { error } = await supabase.from("proposal_items").upsert(
      { collection_id: openId, artwork_id: artworkId, asking_price, rationale, updated_at: new Date().toISOString() },
      { onConflict: "artwork_id" }
    );
    if (error) setMsg(error.message); else { setMsg(""); loadDetail(openId); }
  };

  const createLink = async () => {
    const open = proposals.find((p) => p.id === openId);
    const { error } = await supabase.from("proposal_links").insert({
      collection_id: openId, advisor_id: user.id, token: newProposalToken(), client_label: form.client.trim() || open?.description || null,
    });
    if (error) setMsg(error.message); else { setMsg(""); loadDetail(openId); }
  };

  const revoke = async (id) => {
    await supabase.from("proposal_links").update({ revoked_at: new Date().toISOString() }).eq("id", id);
    loadDetail(openId);
  };

  const copy = async (token) => {
    const url = `${window.location.origin}/proposition/${token}`;
    try { await navigator.clipboard.writeText(url); setMsg("Lien copié."); } catch { setMsg(url); }
  };

  const open = proposals.find((p) => p.id === openId);
  const artTitle = (id) => artworks.find((a) => a.id === id)?.title || "Œuvre";

  return (
    <div style={{ maxWidth: 980, margin: "0 auto", padding: "32px 20px 80px", fontFamily: "Georgia, serif" }}>
      <div style={eyebrow}>Espace Art Advisor</div>
      <h1 style={{ fontSize: 30, margin: "4px 0 6px" }}>Propositions d'acquisition</h1>
      <p style={{ color: "#6b6b6b", fontFamily: "system-ui, sans-serif", fontSize: 14, maxWidth: 680 }}>
        Composez une sélection d'œuvres <strong>non détenues</strong> pour un client. Elle n'entre jamais dans son inventaire.
        Vous partagez un lien valable 14 jours ; il compare 2 ou 3 œuvres et vous renvoie ses réactions.
      </p>
      {msg && <p style={{ fontFamily: "system-ui, sans-serif", fontSize: 13, color: "#8f3a44" }}>{msg}</p>}

      <div style={{ ...card, margin: "20px 0", display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 10, alignItems: "end" }}>
        <div><div style={eyebrow}>Nom de la proposition</div><input style={input} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex. : Sélection printemps" /></div>
        <div>
          <div style={eyebrow}>Client</div>
          <input style={input} list="adv-clients" value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} placeholder="Nom affiché au client" />
          <datalist id="adv-clients">{clients.map((c) => <option key={c.id} value={c.display_name} />)}</datalist>
        </div>
        <button style={btn} onClick={create}>Nouvelle proposition</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 20, alignItems: "start" }}>
        <div style={{ display: "grid", gap: 8 }}>
          {proposals.length === 0 && <div style={{ color: "#9ca3af", fontFamily: "system-ui, sans-serif", fontSize: 13 }}>Aucune proposition pour le moment.</div>}
          {proposals.map((p) => (
            <button key={p.id} onClick={() => setOpenId(p.id)}
              style={{ ...card, textAlign: "left", cursor: "pointer", borderColor: p.id === openId ? "#1a1a1a" : "#e5e3df" }}>
              <div style={{ fontWeight: 600 }}>{p.title}</div>
              <div style={{ color: "#9ca3af", fontSize: 12, fontFamily: "system-ui, sans-serif" }}>{new Date(p.created_at).toLocaleDateString("fr-FR")}</div>
            </button>
          ))}
        </div>

        {open ? (
          <div style={{ display: "grid", gap: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
              <h2 style={{ fontSize: 22, margin: 0 }}>{open.title}</h2>
              <button style={btnGhost} onClick={() => navigate("/add", { state: { preselectedCollectionId: open.id } })}>+ Ajouter une œuvre</button>
            </div>
            {artworks.length === 0 && <div style={{ ...card, color: "#9ca3af", fontFamily: "system-ui, sans-serif", fontSize: 13 }}>Aucune œuvre. Ajoutez-en avec « Ajouter une œuvre » : elles sont créées comme n'importe quelle fiche, dans cette proposition.</div>}
            {artworks.map((a) => <ItemRow key={a.id + (metas[a.id]?.updated_at || "")} artwork={a} meta={metas[a.id]} onSave={saveItem} />)}

            <div style={card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={eyebrow}>Partage au client</div>
                <button style={btn} onClick={createLink} disabled={artworks.length === 0}>Créer un lien (14 jours)</button>
              </div>
              {links.length === 0 && <div style={{ color: "#9ca3af", fontFamily: "system-ui, sans-serif", fontSize: 13 }}>Aucun lien créé.</div>}
              {links.map((l) => {
                const rx = reactions[l.id] || [];
                return (
                  <div key={l.id} style={{ borderTop: "1px solid #f0eee9", padding: "10px 0", fontFamily: "system-ui, sans-serif", fontSize: 13 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                      <span><strong>{linkStatus(l)}</strong> · expire le {new Date(l.expires_at).toLocaleDateString("fr-FR")} · {l.view_count} vue(s)</span>
                      <span style={{ display: "flex", gap: 10 }}>
                        {linkStatus(l) === "Actif" || linkStatus(l) === "Réponses reçues" ? <button onClick={() => copy(l.token)} style={{ background: "none", border: "none", textDecoration: "underline", cursor: "pointer" }}>Copier le lien</button> : null}
                        {!l.revoked_at && <button onClick={() => revoke(l.id)} style={{ background: "none", border: "none", color: "#8f3a44", cursor: "pointer" }}>Révoquer</button>}
                      </span>
                    </div>
                    {rx.length > 0 && (
                      <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                        {rx.map((r) => (
                          <li key={r.artwork_id}><strong>{artTitle(r.artwork_id)}</strong> : {REACTION_LABELS[r.reaction]}{r.comment ? ` — « ${r.comment} »` : ""}</li>
                        ))}
                      </ul>
                    )}
                    {l.submitted_at && <div style={{ color: "#2c5f2d", marginTop: 6 }}>Réponses envoyées le {new Date(l.submitted_at).toLocaleDateString("fr-FR")} (mail récapitulatif envoyé).</div>}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div style={{ color: "#9ca3af", fontFamily: "system-ui, sans-serif", fontSize: 14 }}>Sélectionnez ou créez une proposition.</div>
        )}
      </div>
    </div>
  );
}
